import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { motion } from 'framer-motion';
import { FiBook, FiUsers, FiStar, FiCode, FiHeart, FiTarget, FiZap, FiShield, FiArrowRight } from 'react-icons/fi';
import { FaGraduationCap, FaHandsHelping, FaYoutube } from 'react-icons/fa';
import Footer from './Footer';
import { useTheme } from '../context/ThemeContext';
import { db, collection, getDocs, doc, getDoc } from './firebase';
import SEO from './SEO';
import BackButton from './BackButton';

const FadeIn = ({ children, delay = 0, direction = 'up' }) => {
  const variants = {
    up: { opacity: 0, y: 32 },
    left: { opacity: 0, x: -32 },
    right: { opacity: 0, x: 32 },
  };
  return (
    <motion.div
      initial={variants[direction]}
      whileInView={{ opacity: 1, x: 0, y: 0 }}
      viewport={{ once: true, margin: '-60px' }}
      transition={{ duration: 0.6, delay, ease: 'easeOut' }}
    >
      {children}
    </motion.div>
  );
};

const StatCard = ({ icon, value, label, c }) => (
  <motion.div
    whileHover={{ y: -6 }}
    style={{
      background: c.card,
      border: `1px solid ${c.border}`,
      borderRadius: 16,
      padding: '28px 20px',
      textAlign: 'center',
    }}
  >
    <div style={{
      width: 48, height: 48, borderRadius: 14,
      background: 'rgba(34,197,94,0.1)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      color: '#22c55e', margin: '0 auto 16px', fontSize: 22,
      border: '1px solid rgba(34,197,94,0.1)'
    }}>
      {icon}
    </div>
    <div style={{ fontSize: 28, fontWeight: 900, color: 'var(--text-main)', marginBottom: 4 }}>{value}</div>
    <div style={{ fontSize: 13, color: 'var(--text-muted)', fontWeight: 600 }}>{label}</div>
  </motion.div>
);

const FeatureItem = ({ icon, title, desc, c }) => (
  <div style={{ display: 'flex', gap: 14, marginBottom: 28 }}>
    <div style={{
      width: 42, height: 42, borderRadius: 12, flexShrink: 0,
      background: 'rgba(34,197,94,0.1)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      color: '#22c55e', fontSize: 20, marginTop: 2,
      border: '1px solid rgba(34,197,94,0.1)'
    }}>
      {icon}
    </div>
    <div>
      <div style={{ fontWeight: 800, fontSize: 16, color: 'var(--text-main)', marginBottom: 6 }}>{title}</div>
      <div style={{ fontSize: 13, color: 'var(--text-muted)', lineHeight: 1.7 }}>{desc}</div>
    </div>
  </div>
);

const TeamCard = ({ name, role, initials, c }) => (
  <motion.div
    whileHover={{ y: -4 }}
    style={{
      background: c.card,
      border: `1px solid ${c.border}`,
      borderRadius: 16,
      padding: 24,
      textAlign: 'center',
    }}
  >
    <div style={{
      width: 64, height: 64, borderRadius: '50%',
      background: 'linear-gradient(135deg,#a3e635,#4ade80)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      margin: '0 auto 14px',
      fontSize: 22, fontWeight: 800, color: '#000',
    }}>
      {initials}
    </div>
    <div style={{ fontWeight: 700, color: c.text, fontSize: 15 }}>{name}</div>
    <div style={{ fontSize: 12, color: c.muted, marginTop: 4 }}>{role}</div>
  </motion.div>
);

const About = () => {
  const { isDarkMode } = useTheme();
  const navigate = useNavigate();
  const { user } = useAuth();
  const c = {
    bg: 'var(--bg-main)',
    card: 'var(--bg-card)',
    border: 'var(--border)',
    text: 'var(--text-main)',
    muted: 'var(--text-muted)',
    subtle: 'var(--bg-surface)',
    accent: 'var(--accent)',
  };

  const [statsData, setStatsData] = useState({
    totalUsers: '10K+',
    totalMaterials: '500+',
    averageRating: '4.8★',
    activeProjects: '8'
  });

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const usersSnap = await getDocs(collection(db, 'Users'));
        const totalUsers = usersSnap.size;

        const feedbackSnap = await getDocs(collection(db, 'feedback'));
        let avgRating = 4.8;
        if (feedbackSnap.size > 0) {
          const totalRating = feedbackSnap.docs.reduce((acc, d) => acc + (d.data().rating || 0), 0);
          avgRating = (totalRating / feedbackSnap.size).toFixed(1);
        }

        const postsSnap = await getDocs(collection(db, 'posts'));
        const tutorialsCount = postsSnap.size;

        setStatsData({
          totalUsers: totalUsers > 1000 ? `${(totalUsers / 1000).toFixed(1)}K+` : `${totalUsers}+`,
          totalMaterials: `${tutorialsCount + 150}+`,
          averageRating: `${avgRating}★`,
          activeProjects: '8'
        });

        const statsDoc = await getDoc(doc(db, 'metadata', 'stats'));
        if (statsDoc.exists()) {
          const data = statsDoc.data();
          setStatsData(prev => ({
            ...prev,
            totalUsers: data.totalUsers ? `${(data.totalUsers / 1000).toFixed(1)}K+` : prev.totalUsers,
            totalMaterials: data.totalMaterials ? `${data.totalMaterials}+` : prev.totalMaterials,
            averageRating: data.averageRating ? `${data.averageRating}★` : prev.averageRating,
            activeProjects: data.activeProjects || prev.activeProjects
          }));
        }
      } catch (error) {
        console.error("Error fetching about stats:", error);
      }
    };
    fetchStats();
  }, []);

  const stats = [
    { icon: <FiBook />, value: statsData.totalMaterials, label: 'Study Materials' },
    { icon: <FiUsers />, value: statsData.totalUsers, label: 'Students' },
    { icon: <FiStar />, value: statsData.averageRating, label: 'Average Rating' },
    { icon: <FaGraduationCap />, value: statsData.activeProjects, label: 'Semesters Covered' },
  ];

  const features = [
    { icon: <FiBook />, title: 'Curated Notes', desc: 'Handwritten and typed notes for every subject, reviewed by toppers and faculty.' },
    { icon: <FiCode />, title: 'Previous Year Questions', desc: 'Comprehensive PYQ bank organised semester-wise with expected question patterns.' },
    { icon: <FiZap />, title: 'Video Lectures', desc: 'Curated playlists from best educators mapped to your exact syllabus.' },
    { icon: <FiTarget />, title: 'CodeCampus', desc: 'Free coding tutorials on programming, DSA, web dev, AI/ML and more.' },
    { icon: <FiShield />, title: 'Free & Open', desc: 'All resources are completely free — no paywalls, no subscriptions.' },
    { icon: <FiHeart />, title: 'Community-Driven', desc: 'Built by students, for students. Contribute and help your peers succeed.' },
  ];

  const team = [
    { name: 'SnapED Team', role: 'Founders & Developers', initials: 'SE' },
    { name: 'Contributors', role: 'Notes & PYQ Uploaders', initials: '50+' },
    { name: 'Community', role: 'IPU B.Tech Students', initials: '10K' },
  ];

  return (
    <div style={{ background: c.bg, minHeight: '100vh', transition: 'background 0.3s', paddingTop: '72px' }}>
      {/* Back Button */}
      <div style={{ position: 'absolute', top: '88px', left: '24px', zIndex: 40 }}>
        <BackButton to="/home" label="Back to Home" />
      </div>
      <SEO
        title="About Us - Our Story & Mission"
        description="Learn about SnapED codeCampus - IPU's most trusted open study platform. Our mission is to provide free, high-quality B.Tech notes, PYQs, and coding tutorials to every student."
        keywords="About SnapED, codeCampus Mission, free engineering notes, IPU student platform, GGSIPU community"
      />
      {/* ── HERO ──────────────────────────────────────────────────────── */}
      <section style={{ maxWidth: 900, margin: '0 auto', padding: 'clamp(48px,7vw,100px) 20px clamp(40px,6vw,80px)', textAlign: 'center' }}>
        <FadeIn>
          <div style={{
            display: 'inline-flex', alignItems: 'center', gap: 8,
            padding: '6px 16px', borderRadius: 50,
            background: 'rgba(34,197,94,0.1)',
            border: '1px solid rgba(34,197,94,0.2)',
            fontSize: 11, fontWeight: 800, color: '#22c55e',
            marginBottom: 24, textTransform: 'uppercase', letterSpacing: '0.1em',
          }}>
            🎓 Our Story & Mission
          </div>

          <h1 style={{
            fontSize: 'clamp(2.5rem,6vw,4.5rem)',
            fontWeight: 900, letterSpacing: '-0.04em',
            color: 'var(--text-main)', marginBottom: 24, lineHeight: 1.1,
          }}>
            Built by students,{' '}
            <span style={{
              background: 'linear-gradient(135deg, #22c55e, #16a34a)',
              WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text',
            }}>
              for students
            </span>
          </h1>

          <p style={{ fontSize: 'clamp(14px,1.8vw,17px)', color: c.muted, maxWidth: 620, margin: '0 auto 32px', lineHeight: 1.75 }}>
            SnapED codeCampus is IPU University's most trusted open study platform — offering notes, PYQs,
            video lectures, and coding tutorials completely free to every B.Tech student.
          </p>

          <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap' }}>
            <motion.div
              onClick={() => navigate(user ? '/profile?tab=contribution' : '/login')}
              whileHover={{ scale: 1.02, backgroundColor: '#16a34a' }} whileTap={{ scale: 0.98 }}
              style={{
                height: 48, padding: '0 28px',
                background: '#22c55e', borderRadius: 12,
                color: '#fff', fontSize: 14, fontWeight: 800,
                display: 'flex', alignItems: 'center', gap: 10,
                cursor: 'pointer',
                boxShadow: '0 10px 20px rgba(34,197,94,0.2)'
              }}
            >
              <FaHandsHelping size={18} /> Contribute Resources
            </motion.div>
            <motion.a
              href="https://youtube.com/@snapedcodecampus"
              target="_blank" rel="noopener noreferrer"
              whileHover={{ scale: 1.04 }} whileTap={{ scale: 0.97 }}
              style={{
                height: 44, padding: '0 24px',
                background: 'transparent',
                border: `1px solid ${c.border}`,
                borderRadius: 10,
                color: c.text, fontSize: 14, fontWeight: 600,
                display: 'flex', alignItems: 'center', gap: 8,
                textDecoration: 'none',
              }}
            >
              <FaYoutube size={16} /> YouTube Channel
            </motion.a>
          </div>
        </FadeIn>
      </section>

      {/* ── STATS ─────────────────────────────────────────────────────── */}
      <section style={{ maxWidth: 1200, margin: '0 auto', padding: '0 20px 80px' }}>
        <div className="stats-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(180px,1fr))', gap: 16 }}>
          {stats.map((s, i) => (
            <FadeIn key={s.label} delay={i * 0.1}>
              <StatCard {...s} c={c} />
            </FadeIn>
          ))}
        </div>
      </section>

      {/* ── MISSION ───────────────────────────────────────────────────── */}
      <section style={{ maxWidth: 1200, margin: '0 auto', padding: '0 20px 80px' }}>
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit,minmax(300px,1fr))',
          gap: 32, alignItems: 'center',
        }}>
          <FadeIn direction="left">
            <div style={{
              display: 'inline-block',
              fontSize: 11, fontWeight: 700, textTransform: 'uppercase',
              letterSpacing: '0.12em', color: c.accent, marginBottom: 12,
            }}>
              Our Mission
            </div>
            <h2 style={{ fontSize: 'clamp(1.6rem,3vw,2.6rem)', fontWeight: 800, color: c.text, letterSpacing: '-0.025em', marginBottom: 16 }}>
              Making quality education<br />accessible to all
            </h2>
            <p style={{ fontSize: 14, color: c.muted, lineHeight: 1.8, marginBottom: 28 }}>
              We believe every student deserves access to the best study material, regardless of their financial situation.
              SnapED was created to break down barriers and give every IPU student a fair shot at academic excellence.
            </p>
            <p style={{ fontSize: 14, color: c.muted, lineHeight: 1.8 }}>
              From first semester to final year, we cover notes, previous year questions, video lectures, and coding resources
              for CSE, AIML, AIDS, and IT branches — all completely free.
            </p>
          </FadeIn>

          <FadeIn direction="right">
            <div style={{
              background: c.card,
              border: `1px solid ${c.border}`,
              borderRadius: 20, padding: 32,
            }}>
              {features.map((f, i) => (
                <FeatureItem key={f.title} {...f} c={c} />
              ))}
            </div>
          </FadeIn>
        </div>
      </section>

      {/* ── TEAM ──────────────────────────────────────────────────────── */}
      <section style={{ maxWidth: 1200, margin: '0 auto', padding: '0 20px 80px' }}>
        <FadeIn>
          <div style={{ textAlign: 'center', marginBottom: 40 }}>
            <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.12em', color: c.accent, marginBottom: 10 }}>
              The People Behind It
            </div>
            <h2 style={{ fontSize: 'clamp(1.6rem,3vw,2.4rem)', fontWeight: 800, color: c.text, letterSpacing: '-0.025em' }}>
              Community & Team
            </h2>
          </div>
        </FadeIn>
        <div className="team-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(200px,1fr))', gap: 20 }}>
          {team.map((t, i) => (
            <FadeIn key={t.name} delay={i * 0.1}>
              <TeamCard {...t} c={c} />
            </FadeIn>
          ))}
        </div>
      </section>

      {/* ── CTA BANNER ───────────────────────────────────────────────── */}
      <section style={{ maxWidth: 1200, margin: '0 auto', padding: '0 20px 80px' }}>
        <FadeIn>
          <div style={{
            background: isDarkMode
              ? 'linear-gradient(135deg,rgba(163,230,53,0.08),rgba(74,222,128,0.05))'
              : 'linear-gradient(135deg,rgba(101,163,13,0.06),rgba(52,211,153,0.04))',
            border: `1px solid ${isDarkMode ? 'rgba(163,230,53,0.2)' : 'rgba(101,163,13,0.2)'}`,
            borderRadius: 24,
            padding: 'clamp(32px,5vw,60px)',
            textAlign: 'center',
          }}>
            <div style={{ fontSize: 40, marginBottom: 16 }}>🤝</div>
            <h3 style={{ fontSize: 'clamp(1.4rem,2.5vw,2rem)', fontWeight: 800, color: 'var(--text-main)', marginBottom: 12 }}>
              Help your fellow students
            </h3>
            <p style={{ fontSize: 14, color: c.muted, maxWidth: 480, margin: '0 auto 28px', lineHeight: 1.7 }}>
              Have notes, PYQs, or resources to share? Contribute to SnapED and help thousands of students ace their exams.
            </p>
            <motion.div
              onClick={() => navigate(user ? '/profile?tab=contribution' : '/login')}
              whileHover={{ scale: 1.02, backgroundColor: '#16a34a' }} whileTap={{ scale: 0.98 }}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 10,
                height: 52, padding: '0 32px',
                background: '#22c55e', borderRadius: 14,
                color: '#fff', fontWeight: 800, fontSize: 15,
                cursor: 'pointer',
                boxShadow: '0 10px 20px rgba(34,197,94,0.2)'
              }}
            >
              <FaHandsHelping size={18} /> Start Contributing <FiArrowRight size={16} />
            </motion.div>
          </div>
        </FadeIn>
      </section>

      <Footer />
    </div>
  );
};

export default About;
